/* Help is a side panel, not a modal: the page stays usable beside it (SHELL1). */
.panel {
  display: flex;
  flex: 1 1 360px;
  flex-direction: column;
  min-width: 0;
  max-width: 440px;
  border-left: 1px solid var(--border-card);
  background: var(--white);
  box-shadow: var(--shadow-panel);
  animation: slidein 220ms var(--ease-out) both;
}

/* The Level 2 bar covers the page, never Help beside it or over it (APP-141): while focus is in
   Help, the window has no scroll padding for the bar, so moving focus in Help doesn't move the page
   behind it. */
:global(html):has(.panel :focus) {
  scroll-padding-top: 0;
}

@keyframes slidein {
  from {
    opacity: 0;
    transform: translateX(24px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .panel {
    animation: none;
  }
}

:global(html[data-motion='reduced']) .panel {
  animation: none;
}

/* Narrow windows: the panel covers the right edge instead of wrapping below the page. */
@media (max-width: 960px) {
  .panel {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 60;
    width: min(440px, 100vw);
    max-width: none;
    box-shadow: var(--shadow-drawer);
  }
}

/* Wider for long answers (the widen button); side by side only, so not on narrow windows. */
.wide {
  flex-basis: 720px;
  max-width: 720px;
}

/* --help-top: what is still above the panel in the window (the header until it scrolls away). */
.inner {
  position: sticky;
  top: 0;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - var(--help-top, 0px));
  max-height: calc(100dvh - var(--help-top, 0px));
  overflow-y: auto;
}

.headActions {
  display: flex;
  align-items: center;
  gap: 4px;
}

@media (max-width: 960px) {
  .widen {
    display: none;
  }
}

.head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 16px 8px 22px;
}

.headText {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pageLabel {
  margin: 0;
  color: var(--text-muted);
  font-size: 11px;
}

.title {
  color: var(--text-heading);
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: var(--fw-medium);
  letter-spacing: 0.02em;
  text-transform: uppercase;
  outline: none;
}

.close {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--brand-navy);
  cursor: pointer;
}

.close[data-hovered] {
  background: var(--gray-75);
}

.tabList {
  display: flex;
  padding: 0 12px;
  border-bottom: 1px solid var(--border-card);
}

.tab {
  display: inline-flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: var(--control-height);
  color: var(--text-secondary);
  font-family: var(--font-ui);
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  outline: none;
}

.tab[data-selected] {
  color: var(--brand-navy);
  box-shadow: inset 0 -3px 0 var(--brand-cyan);
}

.tab[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: -2px;
}

.body {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 18px 22px 24px;
  outline: none;
}

.search {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.searchLabel {
  color: var(--text-heading);
  font-size: 13px;
  font-weight: var(--fw-semibold);
}

.searchBox {
  position: relative;
  display: block;
}

.searchIcon {
  position: absolute;
  top: 13px;
  left: 12px;
  color: var(--text-muted);
  pointer-events: none;
}

.searchInput {
  width: 100%;
  min-height: var(--control-height);
  padding: 0 12px 0 36px;
  border: 1px solid var(--border-control);
  border-radius: var(--radius-sm);
  background: var(--white);
  color: var(--text-body);
  font-size: 15px;
}

.searchInput[data-focused] {
  outline: none;
  border-color: var(--brand-cyan);
  box-shadow: var(--focus-halo);
}

.searchInput::-webkit-search-cancel-button {
  display: none;
}

.group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.groupLabel {
  margin: 0;
  color: var(--text-muted);
  font-size: 11px;
}

.topicList {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.topic {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--border-hairline);
  border-radius: 4px;
  background: var(--white);
  color: var(--text-body);
  text-align: left;
  cursor: pointer;
}

.topic[data-hovered] {
  border-color: var(--brand-cyan);
  background: var(--brand-cyan-50);
}

.topicIcon {
  display: grid;
  flex: none;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  background: var(--status-info-bg);
  color: var(--status-info-ink);
}

.topicText {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.topicCategory {
  color: var(--brand-petrol);
  font-size: 10px;
}

.topicTitle {
  color: var(--text-heading);
  font-size: 14px;
  font-weight: var(--fw-bold);
}

.topicSummary {
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.45;
}

.linkButton {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: var(--control-height);
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--brand-navy);
  font-size: 14px;
  font-weight: var(--fw-semibold);
  text-align: left;
  cursor: pointer;
}

.linkButton[data-hovered] {
  color: var(--brand-petrol);
  text-decoration: underline;
}

.plainLinks {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
}

.plainLinks .linkButton {
  font-weight: var(--fw-medium);
}

.article {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.articleCategory {
  margin: 0;
  color: var(--brand-petrol);
  font-size: 11px;
}

.articleTitle {
  color: var(--text-heading);
  font-size: 24px;
  line-height: 1.15;
  outline: none;
}

.articleBody {
  margin: 0;
  color: var(--gray-700);
  font-size: 15px;
  line-height: 1.65;
}

.related {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: 10px;
  border-top: 1px solid var(--border-hairline);
}

.contact {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--border-card);
  border-radius: var(--radius-md);
  background: var(--surface-soft);
}

.separate {
  margin: 0;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.5;
}

.contactIcon {
  display: grid;
  flex: none;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--gray-100);
  color: var(--brand-navy);
}

.contactText {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.contactTitle {
  margin: 0;
  color: var(--text-heading);
  font-size: 14px;
  font-weight: var(--fw-bold);
}

.contactBody {
  margin: 0;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.5;
}

.contactLink {
  display: inline-flex;
  align-items: center;
  min-height: var(--control-height);
  font-size: 14px;
  font-weight: var(--fw-semibold);
}

.headSub {
  margin: 0;
  color: var(--text-secondary);
  font-size: 13px;
}

.loading {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: var(--text-secondary);
  font-size: 14px;
}

.category {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.steps {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding-left: 22px;
  color: var(--gray-700);
  font-size: 15px;
  line-height: 1.55;
}

.start {
  align-self: flex-start;
}

/* "Show me" under a topic about something on this page (APP-090), and where it was highlighted. */
.showMe {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 16px;
  margin: 0;
}

.shownNote {
  flex-basis: 100%;
  color: var(--brand-petrol);
  font-size: 13px;
}

.sent {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 4px 0 0;
  color: var(--status-done-ink);
  font-size: 13px;
  font-weight: var(--fw-semibold);
}

.writeForm {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 6px;
}

.formActions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.error {
  margin: 0;
  color: var(--status-problem-ink);
  font-size: 13px;
}
